import LaunchIcon from '@mui/icons-material/Launch';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { memo, useMemo, useRef, useState } from 'react';
import { ReactComponent as LinkedInIcon } from '../../../../../assets/icons/linkedin.svg';
import useDashboardQueryFetch from '../../../../../hooks/useDashboardQueryFetch';
import theme from '../../../../../theme';
import { Dashboard } from '../../../../../types/files';
import LinkedInPostsModal from '../../../../modals/tables/dashboards/rows/LinkedInPostsModal';
import useStyles from "../styles";

export const useLocalStyles = makeStyles((theme) => ({
    cropped: {
        display: '-webkit-box',
        fontFamily: 'Lato',
        fontSize: '0.8rem',
        fontWeight: 500,
        textDecoration: 'none',
        color: theme.colors.neutral['700'],
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    urlIcon: {
        width: 24,
        height: 24,
        borderRadius: 8,
    },
}));

const LinkedInPostsCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const localClasses = useLocalStyles();
    const { query: linkedInPosts, queryLoaded: linkedInPostsLoaded } = useDashboardQueryFetch<any[]>(dashboard.id, 'LinkedIn Posts');

    const [postsModalOpen, setPostsModalOpen] = useState<boolean>(false);
    const [fieldHover, setFieldHover] = useState<boolean>(false);
    const linkedInPostsRef = useRef<HTMLElement>(null);

    const rawHTML = useMemo(() => {
        const post = linkedInPosts?.[0];
        if (!post)
            return '';

        const text = (post as { content?: string })?.content || "";
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const hasElements = parsed.body.querySelector('*');
        if (!hasElements)
            return '';

        parsed.querySelectorAll('a')?.forEach((link) => {
            link?.setAttribute('target', '_blank');
            link?.setAttribute('rel', 'noopener noreferrer');
            link?.setAttribute('style', `color: #048290; font-weight: bold; text-decoration: none;`);
        });

        return new XMLSerializer().serializeToString(parsed);
    }, [linkedInPosts]);

    return (<>
        <Stack width="100%" ref={linkedInPostsRef} onMouseLeave={() => setFieldHover(false)}>
            {linkedInPostsLoaded ? (!!rawHTML ? (<>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                    onMouseEnter={() => setFieldHover(true)}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        <Box pt="4px">
                            <LinkedInIcon className={classes.icon} fill={'#7bd4d4'} />
                        </Box>
                        <Typography className={localClasses.cropped} dangerouslySetInnerHTML={{ __html: `${rawHTML}`}} />
                    </Stack>
                    {fieldHover && (
                        <Stack direction="row" alignItems="stretch" justifyContent="flex-end">
                            <IconButton disableRipple
                                onClick={() => setPostsModalOpen(true)}
                                sx={{ padding: 'unset !important'}}>
                                <LaunchIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                            </IconButton>
                        </Stack>
                    )}
                </Stack>
            </>) : (
                <Typography className={classes.url} color={`${theme.colors.neutral['500']} !important`}>
                    {'No LinkedIn posts'}
                </Typography>
            )) : (
                <Box sx={{ width: '100%' }}>
                    <Skeleton animation="wave" className={classes.wave} />
                </Box>
            )}
        </Stack>
        {postsModalOpen && (
            <LinkedInPostsModal open={postsModalOpen}
                dashboard={dashboard}
                anchorElement={linkedInPostsRef.current}
                onClose={() => setPostsModalOpen(false)} />
        )}
    </>);
}

export default memo(LinkedInPostsCell);
